import React from "react";
import styled from "styled-components";
import { router } from "@inertiajs/react";
import { Home, ShoppingCart, Globe } from "lucide-react";
import { getMenuItems, getCategories } from "./mock";
import { useLanguage } from "../../../../contexts/language";
import { useOrder } from "../../../../contexts/order";
import { useRoom } from "../../../../contexts/room";
import { RoomInputModal } from "../../../../components/room-input-modal";
import { MenuItem } from "../../../../components/menu-item";
import { CartDrawer } from "../../../../components/cart-drawer";
import { unmodule } from "../../../../util";

const headerLogo = unmodule(require("../../../../assets/logo-dark.png"));

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fff 0%, #f7f7f7 45%, #f5f5f5 100%);
  padding-bottom: 80px;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
  z-index: 2;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 720px;
  margin: 0 auto;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e5e5;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Logo = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 6px;
  object-fit: cover;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: #333;
  font-size: ${(props) => props.$fontSize || "14px"};
  padding: 4px;
  font-weight: ${(props) => props.$fontWeight || "normal"};
`;

const TitleSection = styled.div`
  text-align: center;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 11px;
  color: #666;
  margin: 2px 0 0 0;
`;

const ProfileActionButton = styled.button`
  border: none;
  background: transparent;
  color: #8a8a8a;
  font-size: 11px;
  cursor: pointer;
  margin-top: 2px;
  padding: 0;
  text-decoration: underline;
`;

const CategorySelector = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 12px;
  overflow-x: auto;
  display: flex;
  gap: 8px;
  padding-bottom: 8px;
  padding-top: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategoryButton = styled.button`
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  border-radius: 20px;
  white-space: nowrap;
  background-color: #f0f0f0;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: #c9a961;
    color: #fff;
  }
`;

const ContentContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 18px 12px 24px 12px;
`;

const IntroCard = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
`;

const IntroTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #2f2f2f;
`;

const IntroText = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 4px;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 64px 0;
  color: #999;
`;

const CategorySection = styled.div`
  margin-bottom: 32px;
`;

const CategoryTitle = styled.h2`
  position: sticky;
  top: 95px;
  background-color: #f9f9f9;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  border-bottom: 2px solid #c9a961;
  padding-bottom: 6px;
  padding-top: 6px;
  z-index: 1;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const OutOfStockTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #999;
  margin-top: 40px;
  margin-bottom: 12px;
  border-bottom: 2px solid #e5e5e5;
  padding-bottom: 6px;
`;

const OutOfStockSection = styled.div`
  margin-bottom: 20px;
  opacity: 0.5;
`;

const OutOfStockCategory = styled.h3`
  font-size: 13px;
  font-weight: 500;
  color: #999;
  margin-bottom: 8px;
`;

const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #c9a961;
  color: #fff;
  border: none;
  border-radius: 30px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 169, 97, 0.4);
  font-size: 14px;
  font-weight: 600;
  z-index: 3;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5);
  }
`;

const CartIconWrapper = styled.div`
  position: relative;
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #fff;
  color: #c9a961;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
`;

const UnnamedComponent = (props) => {
  const { building_code } = props;
  const { t, language, changeLanguage } = useLanguage();
  const { getCartItemCount, getCartTotal } = useOrder();
  const { roomNumber, guestName, loadCachedProfile } = useRoom();
  const [items, setItems] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showCart, setShowCart] = React.useState(false);
  const [showRoomModal, setShowRoomModal] = React.useState(false);
  const cartItemCount = getCartItemCount();
  const totalPrice = getCartTotal();

  const toggleLanguage = () => {
    const nextLanguage = language === "en" ? "id" : "en";
    changeLanguage(nextLanguage);
  };

  React.useEffect(() => {
    const cachedLoaded = loadCachedProfile(building_code);
    if (!cachedLoaded && !guestName) {
      setShowRoomModal(true);
    }
  }, [building_code, guestName, loadCachedProfile]);

  React.useEffect(() => {
    const loadMenuData = async () => {
      try {
        const [menuItems, menuCategories] = await Promise.all([
          getMenuItems(),
          getCategories(),
        ]);
        setItems(menuItems);
        setCategories(menuCategories);
      } finally {
        setIsLoading(false);
      }
    };

    loadMenuData();
  }, []);

  const availableItems = items.filter((item) => item.stock > 0);
  const outOfStockItems = items.filter((item) => item.stock === 0);

  const groupByCategory = (itemList) => {
    return categories.reduce((acc, category) => {
      const categoryItems = itemList.filter(
        (item) => item.category === category,
      );
      if (categoryItems.length > 0) {
        acc[category] = categoryItems;
      }
      return acc;
    }, {});
  };

  const availableByCategory = groupByCategory(availableItems);
  const outOfStockByCategory = groupByCategory(outOfStockItems);

  const scrollToCategory = (category) => {
    const element = document.getElementById(
      `category-${category.toLowerCase()}`,
    );
    if (element) {
      const headerHeight = 100;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <PageContainer>
      <Header>
        <TopBar>
          <Brand>
            <IconButton
              onClick={() => {
                router.visit(`/hebe/${building_code}/hgs/`);
              }}
            >
              <Home size={18} />
            </IconButton>
            <Logo src={headerLogo} alt="HEBE" />
          </Brand>
          <TitleSection>
            <Title>{t.order.title}</Title>
            <Subtitle>
              {guestName && roomNumber
                ? `${guestName} • Room ${roomNumber}`
                : guestName || ""}
            </Subtitle>
            <ProfileActionButton onClick={() => setShowRoomModal(true)}>
              Change profile
            </ProfileActionButton>
          </TitleSection>
          <IconButton
            onClick={toggleLanguage}
            $fontSize="12px"
            $fontWeight="500"
          >
            <Globe size={16} />
            <span>{language === "en" ? "EN" : "ID"}</span>
          </IconButton>
        </TopBar>

        <CategorySelector>
          {categories.map((category) => (
            <CategoryButton
              key={category}
              onClick={() => scrollToCategory(category)}
            >
              {category}
            </CategoryButton>
          ))}
        </CategorySelector>
      </Header>

      <ContentContainer>
        {isLoading ? (
          <LoadingContainer>Loading...</LoadingContainer>
        ) : (
          <>
            <IntroCard>
              <IntroTitle>Freshly prepared for you</IntroTitle>
              <IntroText>
                Pick your menu, adjust quantity, add notes, then continue to payment.
              </IntroText>
            </IntroCard>
            {Object.keys(availableByCategory).map((category) => (
              <CategorySection
                key={category}
                id={`category-${category.toLowerCase()}`}
              >
                <CategoryTitle>{category}</CategoryTitle>
                <ItemList>
                  {availableByCategory[category].map((item) => (
                    <MenuItem key={item.id} item={item} />
                  ))}
                </ItemList>
              </CategorySection>
            ))}

            {Object.keys(outOfStockByCategory).length > 0 && (
              <>
                <OutOfStockTitle>{t.order.outOfStock}</OutOfStockTitle>
                {Object.keys(outOfStockByCategory).map((category) => (
                  <OutOfStockSection key={`${category}-oos`}>
                    <OutOfStockCategory>{category}</OutOfStockCategory>
                    <ItemList>
                      {outOfStockByCategory[category].map((item) => (
                        <MenuItem
                          key={item.id}
                          item={item}
                          isOutOfStock={true}
                        />
                      ))}
                    </ItemList>
                  </OutOfStockSection>
                ))}
              </>
            )}
          </>
        )}
      </ContentContainer>

      {cartItemCount > 0 && (
        <FloatingCartButton onClick={() => setShowCart(true)}>
          <CartIconWrapper>
            <ShoppingCart size={20} />
            <CartBadge>{cartItemCount}</CartBadge>
          </CartIconWrapper>
          <span>
            {t.order.cart.checkout} • Rp{Math.round(totalPrice).toLocaleString("id-ID")}
          </span>
        </FloatingCartButton>
      )}

      {showCart && (
        <CartDrawer
          onClose={() => setShowCart(false)}
          building_code={building_code}
        />
      )}
      {showRoomModal && (
        <RoomInputModal
          onRoomVerified={() => setShowRoomModal(false)}
          buildingCode={building_code}
        />
      )}
    </PageContainer>
  );
};

export default UnnamedComponent;
